import {
    Input,
    GridItem,
    Checkbox,
    Tooltip,
    IconButton,
    Icon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    VStack,
    ModalFooter,
    Flex,
    Button,
    Box,
    Grid,
    Text,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import AutoCompleteSelect from 'components/atoms/AutoCompleteSelect';
import { linkStyle } from 'shared/styles';
import { IVisibleProductKeywordByProduct } from 'types';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { TProductKeywordsContainer } from '../ProductKeywordsContainer';
import { useTranslation } from 'helpers/languages/translations';

const ModalFields = ({
    productKeywordsObj,
    modalType,
    keywords,
    handleChangeProductKeyword,
    handleDeleteKeyword,
    languages,
    readonly,
}: TProductKeywordsContainer & {
    productKeywordsObj: IVisibleProductKeywordByProduct | null;
}) => {
    const translate = useTranslation();

    return (
        <Box width="100%">
            {productKeywordsObj && modalType === 'add' && (
                <>
                    <Text fontWeight="bold" mb="10px" fontSize="sm">
                        {translate('product_keyword.column.ean')}
                    </Text>
                    <Input
                        size="sm"
                        onChange={(e) => handleChangeProductKeyword(e.target.value, 'ean')}
                        value={productKeywordsObj.ean}
                        placeholder={translate('product_keyword.column.ean') as string}
                        mb="22px"
                    />
                </>
            )}

            {productKeywordsObj?.keywords && productKeywordsObj.keywords.length > 0 ? (
                <Grid templateColumns="1fr 50px 50px" gap={5} minH="40px" w="100%" alignItems="center" fontSize="sm">
                    <GridItem>
                        <Text fontWeight="bold">{translate('product_keyword.column.kewyord')}</Text>
                    </GridItem>
                    <GridItem>
                        <Text fontWeight="bold">{translate('product_keyword.column.priority')}</Text>
                    </GridItem>
                    <GridItem></GridItem>
                    {productKeywordsObj?.keywords?.map((keyword, index) => {
                        return (
                            <>
                                <GridItem>
                                    <AutoCompleteSelect
                                        size="sm"
                                        placeholder={translate('product_keyword.input.select_keyword')}
                                        onChange={(value) =>
                                            handleChangeProductKeyword(value, 'keyword_id', keyword.keyword_id)
                                        }
                                        value={keyword.keyword_id}
                                        readonly={readonly}
                                    >
                                        {keywords?.keywords.map((k) => (
                                            <option
                                                value={k.keyword_id}
                                                disabled={productKeywordsObj?.keywords.some(
                                                    (k2) =>
                                                        k.keyword_id === k2.keyword_id &&
                                                        k.keyword_id !== keyword.keyword_id,
                                                )}
                                            >
                                                {languages?.length > 1 ? `${k.name} (${k.language})` : k.name}
                                            </option>
                                        ))}
                                    </AutoCompleteSelect>
                                </GridItem>
                                <GridItem textAlign="center">
                                    <Checkbox
                                        onChange={(e) =>
                                            handleChangeProductKeyword(
                                                e.target.value,
                                                'is_priority',
                                                keyword.keyword_id,
                                            )
                                        }
                                        isChecked={Boolean(keyword.is_priority)}
                                        isDisabled={readonly}
                                    ></Checkbox>
                                </GridItem>

                                <GridItem>
                                    <Tooltip label={translate('product_keyword.delete') as string}>
                                        <IconButton
                                            onClick={() => handleDeleteKeyword(keyword.keyword_id as number)}
                                            isDisabled={
                                                (modalType === 'add' && productKeywordsObj.keywords.length === 1) ||
                                                readonly
                                            }
                                            color={
                                                modalType === 'add' && productKeywordsObj.keywords.length === 1
                                                    ? 'gray.200'
                                                    : 'black'
                                            }
                                            size="sm"
                                            backgroundColor={'transparent'}
                                            aria-label="delete keyword"
                                            icon={<Icon as={DeleteIcon} />}
                                        />
                                    </Tooltip>
                                </GridItem>
                            </>
                        );
                    })}
                </Grid>
            ) : (
                <Text color="gray.500" fontSize="sm">
                    {translate('product_keyword.no_keywords')}
                </Text>
            )}
        </Box>
    );
};

const UpdateAndAddModal = (props: TProductKeywordsContainer) => {
    const translate = useTranslation();

    const {
        modalType,
        productKeywordToAdd,
        productKeywordToUpdate,
        updateInitialRef,
        isOpen,
        isAddLoading,
        isUpdateLoading,
        addKeywordToList,
        handleCloseAddAndUpdateModal,
        handleSubmitProductKeywords,
        readonly,
    } = props;

    const productKeywordToUse = modalType === 'add' ? productKeywordToAdd : productKeywordToUpdate;
    const disableAddButton =
        productKeywordToUse?.keywords && productKeywordToUse.keywords.length > 0
            ? productKeywordToUse?.keywords[productKeywordToUse.keywords.length - 1].keyword_id === undefined
            : false;

    const addLinkStyle = disableAddButton ? { cursor: 'not-allowed' } : linkStyle;

    return (
        <Modal initialFocusRef={updateInitialRef} isOpen={isOpen} onClose={handleCloseAddAndUpdateModal}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                    {modalType === 'update'
                        ? `${productKeywordToUse?.upc ?? productKeywordToUse?.ean ?? ''}`
                        : translate('product_keyword.modal.titleAdd')}
                    {modalType === 'update' && (
                        <Text fontSize="sm" color="gray.500">
                            {translate('product_keyword.modal.ean_keywords')}
                        </Text>
                    )}
                </ModalHeader>
                <ModalCloseButton mt="2" isDisabled={modalType === 'add' ? isAddLoading : isUpdateLoading} />

                <ModalBody>
                    {productKeywordToUse && (
                        <VStack>
                            <ModalFields productKeywordsObj={productKeywordToUse} {...props} />
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    {!readonly && (
                        <Box
                            {...addLinkStyle}
                            color={disableAddButton ? 'gray.500' : 'blue.500'}
                            onClick={disableAddButton ? undefined : () => addKeywordToList(modalType)}
                        >
                            {translate('product_keyword.keyword.add')}
                        </Box>
                    )}

                    <Flex flex="1" justifyContent="flex-end">
                        <Button
                            isDisabled={modalType === 'add' ? isAddLoading : isUpdateLoading}
                            size="sm"
                            variant="outline"
                            mr={3}
                            onClick={handleCloseAddAndUpdateModal}
                        >
                            {translate('product_keyword.cancel')}
                        </Button>

                        {!readonly && (
                            <Button
                                onClick={intentionalyFloatingPromiseReturn(handleSubmitProductKeywords)}
                                isLoading={modalType === 'add' ? isAddLoading : isUpdateLoading}
                                size="sm"
                                isDisabled={
                                    modalType === 'add' &&
                                    (productKeywordToAdd.ean === '' ||
                                        productKeywordToAdd.keywords.length === 0 ||
                                        productKeywordToAdd.keywords[0]?.keyword_id === undefined)
                                }
                                colorScheme="blue"
                                bg="blueMain"
                            >
                                {translate('product_keyword.upload.modal.apply')}
                            </Button>
                        )}
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default UpdateAndAddModal;
